export type AuthDiagnosticEvent =
  | 'persistence:set:start'
  | 'persistence:set:success'
  | 'persistence:set:error'
  | 'credential-login:start'
  | 'credential-login:success'
  | 'credential-login:error'
  | 'auth-state:user'
  | 'auth-state:null'
  | 'token-state:user'
  | 'token-state:null'
  | 'token-state:refresh'
  | 'token-state:error'
  | 'session-restore:detected'
  | 'session-storage:changed'
  | 'session-cross-tab:signout-detected'
  | 'profile-read:start'
  | 'profile-read:success'
  | 'profile-read:error'
  | 'profile-create:start'
  | 'profile-create:success'
  | 'profile-create:error'
  | 'profile-bootstrap:start'
  | 'profile-bootstrap:success'
  | 'profile-bootstrap:error'
  | 'profile-bootstrap:retry'
  | 'profile-bootstrap:stale'
  | 'profile-bootstrap:skipped'
  | 'forced-signout:start'
  | 'forced-signout:success'
  | 'forced-signout:error'
  | 'snapshot:error'
  | 'local-storage:summary';

type AuthDiagnosticDetails = Record<string, unknown>;

const importMetaEnv = (import.meta as any)?.env || {};
const SENSITIVE_KEY_PATTERN = /(password|token|credential|secret|authorization|email)/i;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const JWT_LIKE_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const SENSITIVE_ASSIGNMENT_PATTERN =
  /\b(password|passcode|credential|token|idToken|accessToken|refreshToken|secret|authorization)\b\s*[:=]\s*["']?[^"',\s)]+["']?/gi;

const isDiagnosticsEnabled = (): boolean => {
  if (importMetaEnv.DEV) return true;
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('mintleaf.authDiagnostics') === '1';
  } catch {
    return false;
  }
};

const redactString = (value: string): string =>
  value
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(JWT_LIKE_PATTERN, '[redacted-token]')
    .replace(SENSITIVE_ASSIGNMENT_PATTERN, (_match, key) => `${key}=[redacted]`);

export const sanitizeAuthDiagnosticValue = (value: unknown): unknown => {
  if (typeof value === 'string') return redactString(value);
  if (typeof value === 'number' || typeof value === 'boolean' || value == null) return value;
  if (Array.isArray(value)) return value.map(item => sanitizeAuthDiagnosticValue(item));
  if (value instanceof Error) {
    const anyError = value as any;
    return {
      code: typeof anyError.code === 'string' ? redactString(anyError.code) : undefined,
      message: redactString(value.message),
    };
  }
  if (typeof value === 'object') {
    const sanitized: AuthDiagnosticDetails = {};
    Object.entries(value as AuthDiagnosticDetails).forEach(([key, entry]) => {
      sanitized[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? '[redacted]'
        : sanitizeAuthDiagnosticValue(entry);
    });
    return sanitized;
  }
  return undefined;
};

export const authErrorDetails = (error: unknown): AuthDiagnosticDetails => {
  const anyError = error as any;
  return {
    code: typeof anyError?.code === 'string' ? redactString(anyError.code) : 'unknown',
    message: redactString(typeof anyError?.message === 'string' ? anyError.message : String(error)),
  };
};

export const logAuthDiagnostic = (
  event: AuthDiagnosticEvent,
  details: AuthDiagnosticDetails = {}
): void => {
  if (!isDiagnosticsEnabled()) return;
  const safeDetails = sanitizeAuthDiagnosticValue({
    event,
    timestamp: new Date().toISOString(),
    ...details,
  });
  console.info(`[auth-diagnostic] ${event}`, safeDetails);
};

export const summarizeAuthLocalStorage = (): AuthDiagnosticDetails => {
  if (typeof window === 'undefined') {
    return {
      available: false,
      mintleafApiTokenPresent: false,
      unitRestorePresent: false,
      selectedUnitKeyCount: 0,
    };
  }

  try {
    let selectedUnitKeyCount = 0;
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith('mintleaf_selected_units_')) {
        selectedUnitKeyCount += 1;
      }
    }

    return {
      available: true,
      mintleafApiTokenPresent: window.localStorage.getItem('mintleaf_api_token') !== null,
      unitRestorePresent: window.localStorage.getItem('mintleaf.unit.restore') !== null,
      selectedUnitKeyCount,
    };
  } catch (error) {
    return {
      available: false,
      error: authErrorDetails(error),
      mintleafApiTokenPresent: false,
      unitRestorePresent: false,
      selectedUnitKeyCount: 0,
    };
  }
};

export const logAuthLocalStorageSummary = (): void => {
  logAuthDiagnostic('local-storage:summary', summarizeAuthLocalStorage());
};
